import { takeLatest, call, put } from 'redux-saga/effects';
import { push } from 'redux-first-history';

import api from '../api';
import {
    User,
    Role,
    loginRequest,
    loginSuccess,
    loginFailure,
    deleteRequest,
    deleteSuccess,
    deleteFailure,
    signupRequest,
    signupSuccess,
    signupFailure,
    checkEmailRequest,
    checkEmailSuccess,
    checkEmailFailure,
    resetPasswordRequest,
    resetPasswordSuccess,
    resetPasswordFailure,
    logoutRequest,
    LoginResponse,
    SignupResponse,
    ResetPasswordResponse,
    EmailStatusResponse,
    ConstantsResponse,
    constantsRequest,
    constantsSuccess,
    constantsFailure,
    profileRequest,
    profileSuccess,
    profileFailure,
    profileUpdateRequest,
    profileUpdateSuccess,
    profileUpdateFailure,
    rolesRequest,
    rolesSuccess,
    rolesFailure,
    teamsRequest,
    teamsSuccess,
    teamsFailure,
    testimonialRequest,
    testimonialSuccess,
    testimonialFailure,
    Testimonial,
    Team,
    preQualifyRequest,
    featuresRequest,
    featuresSuccess,
    featuresFailure,
    Feature,
    PreQualify,
    listPreQualifyRequest,
    listPreQualifySuccess,
    listPreQualifyFailure,
    tradeinRequest,
    listTradeinRequest,
    listTradeinSuccess,
    listTradeinFailure,
    TradeinRequest,
    CheckVehicleSessionResponse,
    checkVehicleSessionRequest,
    bookletChecklistRequest,
    bookletChecklistSuccess,
    bookletChecklistFailure,
    Checklist,
} from './actions';


// API call
const loginApi = async (credentials: any) => {
    const response = await api.post('/api/auth/login/', credentials);
    return response.data;
};

const deleteApi = async () => {
    const response = await api.post('/api/auth/delete/');
    return response.data;
};

const signupApi = async (credentials: any) => {
    const response = await api.post('/api/auth/signup/', credentials);
    return response.data;
};

const checkEmailApi = async (credentials: any) => {
    const response = await api.post('/api/auth/check_email/', credentials);
    return response.data;
};

const resetPasswordApi = async (credentials: any) => {
    const response = await api.post('/api/auth/reset-password/', credentials);
    return response.data;
};

const constantsApi = async () => {
    const keys = [
        "AUCTION_FIELD_DATA_TYPES",
        "AUCTION_FIELD_FILTER_TYPES",
        "AUCTION_PROVIDERS",
        "AVAILABLE_FIELDS",
        "GOOGLE_OAUTH_HREF",
        "VEHICLE_DRIVE_TYPES",
        "LANGUAGE_CODES",
        "OPENAI_MODELS",
        "PAYMENT_METHODS",
        "DOCUMENT_TYPES",
        "QUALIFICATION_RULES_CONDITIONS",
        "VEHICLE_BODY_STYLES",
        "VEHICLE_DRIVE_TYPES",
        "VEHICLE_FEATURES",
        "VEHICLE_MARKETS",
        "VEHICLE_STATUSES",
        "VEHICLE_TRANSMISSIONS",
        "WEEKDAYS",
        "TITLE_TYPES",
        "WHATSAPP_WEBHOOK_KEY",
        "OUR_PRICE_MIN_PERC",
        "OUR_PRICE_MAX_PERC",
        "OUR_PRICE_MIN_AMOUNT",
        "OUR_PRICE_MAX_AMOUNT",
    ].join(',');
    const response = await api.get('/api/options/constants/?keys=' + keys);
    return response.data;
};

const profileApi = async (): Promise<User> => {
    const response = await api.get('/api/auth/profile/');
    return response.data;
};

const profileUpdateApi = async (data: User): Promise<User> => {
    const response = await api.patch('/api/auth/profile/', data);
    return response.data;
};

const rolesApi = async (): Promise<Role[]> => {
    const response = await api.get('/api/options/roles/');
    return response.data;
};

const teamsApi = async (): Promise<Team[]> => {
    const response = await api.get('/api/options/teams/');
    return response.data;
};

const testimonialsApi = async (): Promise<Testimonial[]> => {
    const response = await api.get('/api/testimonials/');
    return response.data.results;
};

const listPreQualifyApi = async (): Promise<PreQualify[]> => {
    const response = await api.get('/api/pre_qualification/');
    return response.data;
};

const tradeinApi = async (data: Record<string, any>): Promise<any> => {
    const response = await api.post("/api/tradein/", data);
    return response.data;
};

const listTradeinApi = async (): Promise<TradeinRequest[]> => {
    const response = await api.get('/api/tradein/');
    return response.data;
};

const preQualifyApi = async (data: Record<string, any>): Promise<any> => {
    Object.keys(data).forEach((key) => {
        if (key.startsWith('img_')) {
            if (data[key] instanceof FileList) {
                Array.from(data[key]).forEach((file) => {
                    data[key] = file;
                });
            }
        }
    });

    const response = await api.post("/api/pre_qualification/", data, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });

    return response.data;
};

const featuresApi = async (): Promise<Feature[]> => {
    const response = await api.get('/api/options/features/');
    return response.data;
};

const checkVehicleSessionApi = async (data: Record<string, any>): Promise<any> => {
    const response = await api.post("/api/vehicles/check-payment/", data);
    return response.data;
};

const bookletChecklistApi = async (): Promise<Checklist[]> => {
    const response = await api.get('/api/options/booklet-inspection-checklist/');
    return response.data;
};

// Saga for login
function* handleLogin(action: ReturnType<typeof loginRequest>) {
    try {
        const data: LoginResponse = yield call(loginApi, action.payload);

        yield put(loginSuccess(data)); // Dispatch success action

        if (["SuperUser", "Admin"].includes(data.user.role?.title || "")) {
            yield put(push('/dashboard'));
        } else {
            yield put(push('/dashboard/vehicles/inventory'));
        }
    } catch (error: any) {
        let errmsg = error.response?.data?.non_field_errors || 'Network error';
        yield put(loginFailure(errmsg)); // Dispatch failure action
    }
}

// Saga for signupu
function* handleSignup(action: ReturnType<typeof signupRequest>) {
    try {
        const data: SignupResponse = yield call(signupApi, action.payload);

        if (data.result == "success") {
            yield put(signupSuccess(data)); // Dispatch success action
            // Navigate to dashboard after login
            yield put(push('/dashboard'));
        } else {
            yield put(signupFailure(data?.message)); // Dispatch failure action
        }
    } catch (error: any) {
        let errmsg = error.response?.data?.non_field_errors || 'Network error';
        yield put(signupFailure(errmsg)); // Dispatch failure action
    }
}

// Saga for checkEmail
function* handleCheckEmail(action: ReturnType<typeof checkEmailRequest>) {
    try {
        const data: EmailStatusResponse = yield call(checkEmailApi, action.payload);

        yield put(checkEmailSuccess(data)); // Dispatch success action
    } catch (error: any) {
        let errmsg = error.response?.data?.non_field_errors || 'Network error';
        yield put(checkEmailFailure(errmsg)); // Dispatch failure action
    }
}

// Saga for resetPassword
function* handleResetPassword(action: ReturnType<typeof resetPasswordRequest>) {
    try {
        const data: ResetPasswordResponse = yield call(resetPasswordApi, action.payload);

        if (data?.result == "success") {
            yield put(resetPasswordSuccess(true)); // Dispatch success action
        }
    } catch (error: any) {
        let errmsg = error.response?.data?.non_field_errors || 'Network error';
        yield put(resetPasswordFailure(errmsg)); // Dispatch failure action
    }
}

// Saga for logout
function* handleLogout() {
    try {
        yield put(push('/login'));     // Navigate to login after logout
    } catch (error: any) {
        yield put(loginFailure(error.response?.data)); // Dispatch failure action
    }
}

// Saga for System stats
function* handleConstants() {
    try {
        const data: ConstantsResponse = yield call(constantsApi);

        yield put(constantsSuccess(data)); // Dispatch success action
    } catch (error: any) {
        yield put(constantsFailure(error.response?.data)); // Dispatch failure action
    }
}

// Saga for System stats
function* handleProfile() {
    try {
        const data: User = yield call(profileApi);

        yield put(profileSuccess(data)); // Dispatch success action
    } catch (error: any) {
        yield put(profileFailure(error.response?.data)); // Dispatch failure action
    }
}

function* handleProfileUpdate(action: ReturnType<typeof profileUpdateRequest>) {
    try {
        const data: User = yield call(profileUpdateApi, action.payload);

        yield put(profileUpdateSuccess(data)); // Dispatch success action
    } catch (error: any) {
        yield put(profileUpdateFailure(error.response?.data)); // Dispatch failure action
    }
}

// Saga for Delete Accounut
function* handleDelete() {
    try {
        yield call(deleteApi);
        yield put(deleteSuccess()); // Dispatch success action
        yield put(push('/login')); // Navigate to dashboard after login
    } catch (error: any) {
        let errmsg = error.response?.data?.non_field_errors || 'Network error';
        yield put(deleteFailure(errmsg)); // Dispatch failure action
    }
}

function* handleRoles() {
    try {
        const data: Role[] = yield call(rolesApi);

        yield put(rolesSuccess(data)); // Dispatch success action
    } catch (error: any) {
        yield put(rolesFailure(error.response?.data)); // Dispatch failure action
    }
}

function* handleTeams() {
    try {
        const data: Team[] = yield call(teamsApi);

        yield put(teamsSuccess(data)); // Dispatch success action
    } catch (error: any) {
        yield put(teamsFailure(error.response?.data)); // Dispatch failure action
    }
}

function* handleTestimonials() {
    try {
        const data: Testimonial[] = yield call(testimonialsApi);

        yield put(testimonialSuccess(data)); // Dispatch success action
    } catch (error: any) {
        yield put(testimonialFailure(error.response?.data)); // Dispatch failure action
    }
}

function* handlePreQualify(action: ReturnType<typeof preQualifyRequest>) {
    try {
        yield call(preQualifyApi, action.payload.data);
        if (action.payload.cb) {
            action.payload.cb(true);
        }
    } catch (error: any) {
        if (action.payload.cb) {
            action.payload.cb(false);
        }
    }
}

function* handleFeatures() {
    try {
        const data: Feature[] = yield call(featuresApi);

        yield put(featuresSuccess(data)); // Dispatch success action
    } catch (error: any) {
        yield put(featuresFailure(error.response?.data)); // Dispatch failure action
    }
}

function* handleListPreQualify() {
    try {
        const data: PreQualify[] = yield call(listPreQualifyApi);
        yield put(listPreQualifySuccess(data)); // Dispatch success action
    } catch (error: any) {
        yield put(listPreQualifyFailure(error.response?.data)); // Dispatch failure action
    }
}

function* handleTradein(action: ReturnType<typeof tradeinRequest>) {
    try {
        yield call(tradeinApi, action.payload.data);

        if (action.payload.cb) {
            action.payload.cb(true);
        }
    } catch (error: any) {
        if (action.payload.cb) {
            action.payload.cb(false);
        }
    }
}

function* handleListTradein() {
    try {
        const data: TradeinRequest[] = yield call(listTradeinApi);
        yield put(listTradeinSuccess(data)); // Dispatch success action
    } catch (error: any) {
        yield put(listTradeinFailure(error.response?.data)); // Dispatch failure action
    }
}

function* handleCheckVehicleSession(action: ReturnType<typeof checkVehicleSessionRequest>) {
    try {
        const data: CheckVehicleSessionResponse = yield call(checkVehicleSessionApi, action.payload);
        if (action.payload.cb) {
            action.payload.cb(data);
        }
    } catch (error: any) {
        if (action.payload.cb) {
            action.payload.cb({ } as CheckVehicleSessionResponse);
        }
    }
}

function* handleBookletChecklist() {
    try {
        const data: Checklist[] = yield call(bookletChecklistApi);
        yield put(bookletChecklistSuccess(data)); // Dispatch success action
    } catch (error: any) {
        yield put(bookletChecklistFailure(error.response?.data)); // Dispatch failure action
    }
}
// Watcher Saga
export function* watchAuthSaga() {
    yield takeLatest(loginRequest.type, handleLogin);   // Listen for login request
    yield takeLatest(signupRequest.type, handleSignup);   // Listen for login request
    yield takeLatest(checkEmailRequest.type, handleCheckEmail);   // Listen for login request
    yield takeLatest(resetPasswordRequest.type, handleResetPassword);   // Listen for login request
    yield takeLatest(logoutRequest.type, handleLogout); // Listen for logout request
    yield takeLatest(constantsRequest.type, handleConstants); // Listen for logout request
    yield takeLatest(profileRequest.type, handleProfile); // Listen for logout request
    yield takeLatest(profileUpdateRequest.type, handleProfileUpdate); // Listen for logout request
    yield takeLatest(deleteRequest.type, handleDelete); // Listen for delete request
    yield takeLatest(rolesRequest.type, handleRoles); // Listen for delete request
    yield takeLatest(teamsRequest.type, handleTeams); // Listen for delete request
    yield takeLatest(testimonialRequest.type, handleTestimonials); // Listen for delete request
    yield takeLatest(preQualifyRequest.type, handlePreQualify); // Listen for delete request
    yield takeLatest(featuresRequest.type, handleFeatures); // Listen for delete request
    yield takeLatest(listPreQualifyRequest.type, handleListPreQualify); // Listen for delete request
    yield takeLatest(tradeinRequest.type, handleTradein); // Listen for delete request
    yield takeLatest(listTradeinRequest.type, handleListTradein); // Listen for delete request
    yield takeLatest(checkVehicleSessionRequest.type, handleCheckVehicleSession); // Listen for delete request
    yield takeLatest(bookletChecklistRequest.type, handleBookletChecklist); // Listen for delete request
}